/**
 * Date Formatting Utilities
 *
 * Provides functions to format dates without timezone interference.
 * Used for date-only fields (startDate, endDate) where only the calendar
 * date matters, not the specific time or timezone.
 */

import { Timestamp } from 'firebase/firestore';

/**
 * Format a date ignoring timezone
 *
 * This function is specifically for dates that represent calendar dates only
 * (like cycle start/end dates), not timestamps with specific times.
 *
 * Problem: When a date is stored as "2025-01-01 00:00:00 UTC" and we're in
 * UTC-3 (Brazil), toLocaleDateString() converts it to 2024-12-31 21:00:00
 * local time, showing "31 de dez. de 2024" instead of "01 de jan. de 2025".
 *
 * Solution: Extract UTC components (year, month, day) and create a new local
 * date with those components, then format it. This ensures "2025-01-01 UTC"
 * is always displayed as "01/01/2025" regardless of timezone.
 *
 * @param date - Date object to format
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @returns Formatted date string
 *
 * @example
 * // Date stored as 2025-01-01 00:00:00 UTC
 * const date = new Date('2025-01-01T00:00:00Z');
 * formatDateIgnoringTimezone(date, { day: '2-digit', month: 'short', year: 'numeric' });
 * // Returns: "01 de jan. de 2025" (not "31 de dez. de 2024")
 */
export function formatDateIgnoringTimezone(
  date: Date,
  options: Intl.DateTimeFormatOptions
): string {
  // Create a new date using UTC components as local time
  const utcDate = new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate()
  );

  return utcDate.toLocaleDateString('pt-BR', options);
}

/**
 * Format a date range ignoring timezone
 *
 * Formats two dates as a range (e.g., "01 de jan. de 2025 - 31 de mar. de 2025").
 * Uses formatDateIgnoringTimezone internally to avoid timezone issues.
 *
 * @param startDate - Start date (Date or Timestamp)
 * @param endDate - End date (Date or Timestamp)
 * @param options - Optional Intl.DateTimeFormatOptions for formatting
 * @returns Formatted date range string
 *
 * @example
 * const start = Timestamp.fromDate(new Date('2025-01-01'));
 * const end = Timestamp.fromDate(new Date('2025-03-31'));
 * formatDateRangeIgnoringTimezone(start, end);
 * // Returns: "01 de jan. de 2025 - 31 de mar. de 2025"
 */
export function formatDateRangeIgnoringTimezone(
  startDate: Timestamp | Date,
  endDate: Timestamp | Date,
  options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }
): string {
  const startDateObj = startDate instanceof Timestamp ? startDate.toDate() : startDate;
  const endDateObj = endDate instanceof Timestamp ? endDate.toDate() : endDate;

  const start = formatDateIgnoringTimezone(startDateObj, options);
  const end = formatDateIgnoringTimezone(endDateObj, options);

  return `${start} - ${end}`;
}
